/**
 * Pagina pubblica di conferma disiscrizione (redirect dal backend dopo il click sul link email).
 *
 * Legge la query string:
 *   - status = success | error        (default: success)
 *   - scope  = global | entity        (global = tutte le newsletter; entity = solo l'entità mittente)
 *   - entity = <nome entità>          (solo per scope=entity, per personalizzare il messaggio)
 *
 * Pagina statica: la disiscrizione è già stata processata dal BE; qui si mostra solo l'esito.
 */
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Typography, Container, Box, Card } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CenterContent } from '../utils/LayoutStyles';
import { DefaultContainedButton } from '../utils/Buttons';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') === 'error' ? 'error' : 'success';
  const scope = searchParams.get('scope'); // 'global' | 'entity'
  const entity = searchParams.get('entity'); // nome entità (solo scope=entity)

  const isError = status === 'error';
  const isEntity = scope === 'entity';

  let title;
  let message;

  if (isError) {
    title = 'Disiscrizione non riuscita';
    message =
      'Si è verificato un errore durante la disiscrizione. Il link potrebbe essere scaduto o non valido. Riprova più tardi.';
  } else {
    title = 'Disiscrizione completata';
    message = isEntity
      ? `Non riceverai più le newsletter${entity ? ` di ${entity}` : ' di questo mittente'}.`
      : 'Non riceverai più le nostre newsletter.';
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Container className="default-bg" sx={{ height: '100%', width: '100%', maxWidth: 'unset !important' }}>
        <CenterContent>
          <Card sx={{ padding: '40px', maxWidth: 480, textAlign: 'center' }}>
            <Box
              component="img"
              src="/assets/images/img/logopd.png"
              alt="Logo PD"
              sx={{ width: 150, mx: 'auto', mb: 4, display: 'block' }}
            />

            <Typography variant="h3" paragraph>
              {title}
            </Typography>

            {isError ? (
              <ErrorOutlineIcon sx={{ fontSize: 72, color: '#B72136', mb: 3 }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#1B806A', mb: 3 }} />
            )}

            <Typography sx={{ color: 'text.secondary' }}>{message}</Typography>

            {!isError && (
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                Se hai cambiato idea, puoi gestire le tue preferenze di comunicazione dal tuo profilo.
              </Typography>
            )}

            <Box sx={{ mt: 4 }}>
              <DefaultContainedButton to="/" component={RouterLink}>
                Vai all&apos;accesso
              </DefaultContainedButton>
            </Box>
          </Card>
        </CenterContent>
      </Container>
    </>
  );
}
